import React, { useCallback, useEffect, useRef, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { getUrlDataReports, requeueUrlDataReport, updateUrlDataReport } from '../../api/url_data';
import ConfirmPrompt from '../General/ConfirmPrompt';
import CatalogToolSubNav from '../CatalogTool/CatalogToolSubNav';
import UrlDataReportsControls from './UrlDataReportsControls';
import UrlDataReportsResults from './UrlDataReportsResults';
import './UrlDataReports.scss';

const PAGE_SIZE = 50;

const UrlDataReports = () => {
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('queue');
  const [liveOnly, setLiveOnly] = useState(false);
  const [wrongBrandOnly, setWrongBrandOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const requestIdRef = useRef(0);

  const setBusy = (id, isBusy) =>
    setBusyIds(prev => {
      const next = new Set(prev);
      if (isBusy) next.add(id);
      else next.delete(id);
      return next;
    });

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await getUrlDataReports({
        page,
        limit: PAGE_SIZE,
        status,
        hasActiveOpportunity: liveOnly || undefined,
        wrongBrand: wrongBrandOnly || undefined
      });
      // Ignore stale responses if the page or filters changed during the request
      if (requestId !== requestIdRef.current) return;
      setReports(res?.data?.rows || []);
      setTotal(res?.data?.total || 0);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err || 'Failed to load reports');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [page, status, liveOnly, wrongBrandOnly]);

  // Handlers refresh through the ref so a mutation that finishes after a tab/page
  // change re-fetches with the current filters, not the ones captured at click time.
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
    load();
  }, [load]);

  const runUpdate = async (report, updates) => {
    setBusy(report.id, true);
    try {
      await updateUrlDataReport(report.id, updates);
      await loadRef.current();
    } catch (err) {
      window.ALERT.error(err || 'Failed to update report');
    } finally {
      setBusy(report.id, false);
    }
  };

  const claim = report => runUpdate(report, { Assignee_id: 'me' });

  const resolve = report => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header='Resolve this report?'
          subheader='Optional note on what you did.'
          placeholder='Rematched to Honest, dismissed as retailer house brand, …'
          submitBtnDisplay='Resolve'
          onCancel={onClose}
          onSubmit={resolution => {
            onClose();
            runUpdate(report, { status: 'resolved', resolution, Assignee_id: 'me' });
          }}
        />
      )
    });
  };

  const dismiss = report => {
    confirmAlert({
      title: 'Dismiss this report?',
      message: 'Use this when the match is already correct or the report is noise.',
      buttons: [
        { label: 'Cancel', onClick: () => {} },
        { label: 'Dismiss', onClick: () => runUpdate(report, { status: 'dismissed', Assignee_id: 'me' }) }
      ]
    });
  };

  const requeue = report => {
    if (!report.hashedUrlStem) {
      window.ALERT.error('This report has no URL stem to re-queue.');
      return;
    }
    confirmAlert({
      title: 'Clear match and re-queue?',
      message:
        'This clears Product_id on the URL and its pins, then puts the stem back on the match queue so it can be rematched. Live-opportunity URLs go to the front.',
      buttons: [
        { label: 'Cancel', onClick: () => {} },
        {
          label: 'Re-queue',
          onClick: async () => {
            setBusy(report.id, true);
            try {
              const res = await requeueUrlDataReport(report.id);
              const pinsSuffix = res?.pinsUpdated ? `, ${res.pinsUpdated} pins unmatched` : '';
              if (res?.requeued) {
                window.ALERT.success(`Re-queued${pinsSuffix}`);
              } else if (res?.sentForRecrawl) {
                window.ALERT.success(`Match cleared${pinsSuffix} — the URL recrawls before it can be rematched`);
              } else {
                window.ALERT.warn(`Match cleared${pinsSuffix}, but the URL is not eligible for the match queue`);
              }
              await loadRef.current();
            } catch (err) {
              window.ALERT.error(err || 'Failed to re-queue');
            } finally {
              setBusy(report.id, false);
            }
          }
        }
      ]
    });
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const changeStatus = next => {
    setStatus(next);
    setPage(1);
  };

  return (
    <div className='url-data-reports'>
      <CatalogToolSubNav />
      <header className='udr-header'>
        <h1 className='udr-title'>Catalog Reports</h1>
      </header>
      <UrlDataReportsControls
        status={status}
        onStatusChange={changeStatus}
        liveOnly={liveOnly}
        onToggleLiveOnly={() => {
          setLiveOnly(v => !v);
          setPage(1);
        }}
        wrongBrandOnly={wrongBrandOnly}
        onToggleWrongBrandOnly={() => {
          setWrongBrandOnly(v => !v);
          setPage(1);
        }}
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
      />
      {error && <div className='udr-error'>{error}</div>}
      <UrlDataReportsResults
        reports={reports}
        loading={loading}
        busyIds={busyIds}
        onRequeue={requeue}
        onResolve={resolve}
        onDismiss={dismiss}
        onClaim={claim}
      />
    </div>
  );
};

export default UrlDataReports;
